import { NavLink, Outlet } from 'react-router-dom';
import { CalendarDays, FileText, Flower2, MapPin, MessageCircle } from 'lucide-react';

import { LoginArea } from '@/components/auth/LoginArea';
import { cn } from '@/lib/utils';

const NAV_ITEMS = [
  { to: '/', label: 'Tracker', icon: CalendarDays, end: true },
  { to: '/report', label: 'Report', icon: FileText, end: false },
  { to: '/care', label: 'Care', icon: MapPin, end: false },
  { to: '/community', label: 'Community', icon: MessageCircle, end: false },
] as const;

export function AppLayout() {
  return (
    <div className="min-h-dvh bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/70 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-2xl items-center justify-between gap-3 px-4">
          <div className="flex items-center gap-2.5">
            <span className="flex size-10 items-center justify-center rounded-2xl bg-gradient-to-br from-blush-strong via-lavender-strong to-aqua-strong shadow-sm">
              <Flower2 className="size-5 text-white" aria-hidden />
            </span>
            <div className="leading-tight">
              <p className="text-lg font-bold tracking-tight">Better Me</p>
            </div>
          </div>
          <LoginArea className="max-w-36" />
        </div>
      </header>

      {/* Page content */}
      <main className="mx-auto w-full max-w-2xl px-4 pb-36 pt-5">
        <Outlet />

        <p className="mt-10 text-center text-xs text-muted-foreground">
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline-offset-2 hover:underline"
          >
            Vibed with Shakespeare
          </a>
        </p>
      </main>

      {/* Bottom navigation */}
      <nav
        aria-label="Main navigation"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border/70 bg-white/92 backdrop-blur-md"
      >
        <div className="mx-auto grid max-w-2xl grid-cols-4 gap-1 px-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2">
          {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center gap-1 rounded-2xl px-2 py-2 text-[11px] font-semibold transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      'flex size-11 items-center justify-center rounded-2xl transition-colors',
                      isActive ? 'bg-blush text-blush-foreground shadow-sm' : 'bg-transparent',
                    )}
                  >
                    <Icon className="size-6" aria-hidden />
                  </span>
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
